import DecorSvg from "@/components/stationery/DecorSvg";
import { SITE_THEMES, SECTION_ORDER } from "@/lib/siteThemes";
import { fdateShort } from "@/lib/format";

function Divider({ t }) {
  if (t.divider === "line") return <div className="mx-auto my-6 h-px w-16" style={{ background: t.accent }} />;
  return <div className="mx-auto my-5" style={{ width: 130, height: 34, color: t.accent }}>
    <DecorSvg motif={t.divider} color={t.accent} /></div>;
}

function Eyebrow({ children, t, className = "" }) {
  return <div className={`text-[11px] mb-3 ${t.caps ? "uppercase" : ""} ${className}`}
    style={{ letterSpacing: t.caps ? "0.32em" : "0.06em", color: t.accent, fontFamily: t.body }}>{children}</div>;
}

function Heading({ children, t, size = "text-3xl sm:text-4xl" }) {
  return <div className={size} style={{ fontFamily: t.heading, color: t.text, fontWeight: t.weight, lineHeight: 1.08 }}>{children}</div>;
}

function Section({ children, t, alt, id }) {
  return <section id={id} className="px-6 sm:px-10 py-14 sm:py-20" style={{ background: alt ? t.surface : "transparent" }}>
    <div className="max-w-3xl mx-auto">{children}</div></section>;
}

export default function SiteRenderer({ themeKey, config, wedding, device = "desktop" }) {
  const t = SITE_THEMES[themeKey] || SITE_THEMES.botanical;
  const s = config?.sections || {};
  const order = (config?.order && config.order.length) ? config.order : SECTION_ORDER;
  const center = t.nav !== "left" ? "text-center" : "text-left";
  const small = device === "mobile";

  const dateFr = fdateShort(s.home?.date || wedding?.wedding_date);
  const city = s.home?.city || wedding?.city;

  const Hero = () => {
    const h = s.home || {};
    const photo = h.photo;
    const inner = (
      <div className={`${center} ${photo ? "text-white" : ""}`} style={!photo ? { color: t.text } : undefined}>
        <Eyebrow t={t} className={photo ? "!text-white/80" : ""}>{h.eyebrow || "Nous nous marions"}</Eyebrow>
        <div style={{ fontFamily: t.heading, fontWeight: t.weight, lineHeight: 1.02 }}
          className={small ? "text-4xl" : "text-5xl sm:text-6xl"}>{h.title || wedding?.names}</div>
        <div className="mt-5" style={{ fontFamily: t.body, letterSpacing: "0.05em" }}>
          {dateFr}{city ? ` · ${city}` : ""}</div>
      </div>
    );
    if (photo && (t.hero === "overlay" || t.hero === "framed"))
      return (
        <div className="relative flex items-center justify-center px-6" style={{ minHeight: small ? 380 : 560 }}>
          <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,.55), rgba(0,0,0,.15))" }} />
          <div className="relative z-10 py-16">{t.hero === "framed" ? <div className="border border-white/60 px-8 py-12">{inner}</div> : inner}</div>
        </div>
      );
    if (t.hero === "split")
      return (
        <div className={`grid ${small ? "" : "sm:grid-cols-2"} items-stretch`}>
          <div className="flex items-center px-8 sm:px-12 py-16" style={{ background: t.surface }}>{inner}</div>
          <div className="min-h-[300px]" style={{ background: photo ? undefined : t.accent }}>
            {photo && <img src={photo} alt="" className="w-full h-full object-cover" style={{ minHeight: 300 }} />}
          </div>
        </div>
      );
    return <div className="px-6 py-20 sm:py-28" style={{ background: t.hero === "solid" ? t.surface : "transparent" }}>{inner}</div>;
  };

  return (
    <div style={{ background: t.bg, color: t.text, fontFamily: t.body }} data-testid="site-render">
      {order.map((key) => {
        const sec = s[key];
        if (key !== "home" && (!sec || !sec.enabled)) return null;
        if (key === "home") return sec?.enabled === false ? null : <div key={key}>{Hero()}</div>;

        if (key === "story")
          return <Section key={key} t={t} alt><Eyebrow t={t}>À propos</Eyebrow><Heading t={t}>{sec.title}</Heading><Divider t={t} />
            <div className={`grid ${sec.photo && !small ? "sm:grid-cols-[1fr_240px]" : ""} gap-8 items-center`}>
              <p className="leading-relaxed whitespace-pre-wrap" style={{ fontFamily: t.body, color: t.muted, fontSize: 16 }}>{sec.text}</p>
              {sec.photo && <img src={sec.photo} alt="" className="w-full object-cover" style={{ borderRadius: t.radius, maxHeight: 320 }} />}
            </div></Section>;

        if (key === "schedule")
          return <Section key={key} t={t}><div className={center}><Heading t={t}>{sec.title}</Heading><Divider t={t} /></div>
            <div className="space-y-6 mt-4">{(sec.items || []).map((it, i) => (
              <div key={i} className="flex gap-5">
                <div className="w-16 shrink-0 text-right" style={{ fontFamily: t.heading, color: t.accent, fontSize: 18 }}>{it.time}</div>
                <div className="border-l pl-5 pb-1" style={{ borderColor: t.accent + "55" }}>
                  <div style={{ fontFamily: t.heading, fontSize: 19 }}>{it.title}</div>
                  {it.location && <div className="text-sm mt-0.5" style={{ color: t.accent }}>{it.location}</div>}
                  {it.desc && <div className="text-sm mt-1" style={{ color: t.muted }}>{it.desc}</div>}
                </div></div>))}</div></Section>;

        if (key === "locations")
          return <Section key={key} t={t} alt><div className={center}><Heading t={t}>{sec.title}</Heading><Divider t={t} /></div>
            <div className={`grid ${small ? "" : "sm:grid-cols-2"} gap-5 mt-4`}>{(sec.items || []).map((it, i) => (
              <div key={i} className="p-6" style={{ background: t.bg, borderRadius: t.radius, border: `1px solid ${t.accent}22` }}>
                <div style={{ fontFamily: t.heading, fontSize: 20 }}>{it.name}</div>
                {it.address && <div className="text-sm mt-1" style={{ color: t.accent }}>{it.address}</div>}
                {it.desc && <div className="text-sm mt-2" style={{ color: t.muted }}>{it.desc}</div>}
                {it.mapUrl && <a href={it.mapUrl} target="_blank" rel="noreferrer" className="inline-block text-xs mt-3 underline" style={{ color: t.accent }}>Voir sur la carte</a>}
              </div>))}</div></Section>;

        if (key === "accommodations")
          return <Section key={key} t={t}><div className={center}><Heading t={t}>{sec.title}</Heading><Divider t={t} /></div>
            <div className="space-y-4 mt-4">{(sec.items || []).map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-4 p-5" style={{ border: `1px solid ${t.accent}22`, borderRadius: t.radius }}>
                <div><div style={{ fontFamily: t.heading, fontSize: 18 }}>{it.name}</div>
                  {it.desc && <div className="text-sm mt-1" style={{ color: t.muted }}>{it.desc}</div>}
                  {it.note && <div className="text-xs mt-1" style={{ color: t.accent }}>{it.note}</div>}</div>
                {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="text-xs underline shrink-0" style={{ color: t.accent }}>Réserver</a>}
              </div>))}</div></Section>;

        if (key === "practical")
          return <Section key={key} t={t} alt><div className={center}><Heading t={t}>{sec.title}</Heading><Divider t={t} /></div>
            <div className={`grid ${small ? "" : "sm:grid-cols-2"} gap-5 mt-4`}>{(sec.blocks || []).map((b, i) => (
              <div key={i}><div className="text-sm font-semibold mb-1" style={{ color: t.accent, fontFamily: t.body }}>{b.title}</div>
                <div className="text-sm leading-relaxed" style={{ color: t.muted }}>{b.text}</div></div>))}</div></Section>;

        if (key === "gallery")
          return <Section key={key} t={t}><div className={center}><Heading t={t}>{sec.title}</Heading><Divider t={t} /></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">{(sec.photos || []).map((p, i) => (
              <img key={i} src={p} alt="" className="w-full aspect-square object-cover" style={{ borderRadius: t.radius }} />))}
              {(sec.photos || []).length === 0 && <div className="col-span-full text-center text-sm py-8" style={{ color: t.muted }}>Ajoutez vos plus belles photos.</div>}</div></Section>;

        if (key === "faq")
          return <Section key={key} t={t} alt><div className={center}><Heading t={t}>{sec.title}</Heading><Divider t={t} /></div>
            <div className="space-y-5 mt-4">{(sec.items || []).filter((q) => q.q).map((q, i) => (
              <div key={i}><div style={{ fontFamily: t.heading, fontSize: 17 }}>{q.q}</div>
                {q.a && <div className="text-sm mt-1 leading-relaxed" style={{ color: t.muted }}>{q.a}</div>}</div>))}</div></Section>;

        if (key === "rsvp")
          return <Section key={key} t={t}><div className="text-center p-8 sm:p-12" style={{ border: `1px solid ${t.accent}44`, borderRadius: t.radius }}>
            <Eyebrow t={t}>RSVP</Eyebrow><Heading t={t} size="text-3xl">{sec.title}</Heading><Divider t={t} />
            <p className="max-w-lg mx-auto leading-relaxed" style={{ color: t.muted }}>{sec.text}</p>
            {sec.deadline && <p className="mt-4 text-sm" style={{ color: t.accent }}>Réponse souhaitée avant le {fdateShort(sec.deadline)}</p>}
            <div className="mt-6 inline-block px-7 py-3 text-sm" style={{ background: t.accent, color: t.accentText, borderRadius: t.radius === "0px" ? "0" : "999px", fontFamily: t.body }}>
              Répondre avec votre lien personnel</div>
            <p className="mt-3 text-xs" style={{ color: t.muted }}>Utilisez le lien reçu dans votre invitation.</p>
          </div></Section>;
        return null;
      })}
      <footer className="text-center py-8 text-xs" style={{ color: t.muted, fontFamily: t.body }}>
        {wedding?.names} · {dateFr}</footer>
    </div>
  );
}
